/**
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║  SOCIALSHARING.TSX - Social Media Integration & Sharing                   ║
 * ║  One-click sharing to Twitter/X with pre-written copy                     ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 */

import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { reportGameEvent } from '../utils/gameEngine';
import BodyPortal from './BodyPortal';
import { createPortal } from 'react-dom';

interface ShareData {
  type: 'purchase' | 'win' | 'dividend' | 'referral' | 'title' | 'general' | 'story' | 'streak' | 'bounty' | 'mission';
  amount?: string;
  keys?: string;
  title?: string;
  rank?: number;
  narrative?: string;
  streak?: number;
}

interface SocialSharingProps {
  data?: ShareData;
  className?: string;
  playerAddress?: string;
}

// Pre-written share messages
const SHARE_MESSAGES = {
  purchase: (data: ShareData) => [
    `🔑 Just grabbed ${data.keys} keys in No Tomorrow 3D! The degen life chose me. Are you playing? 🎮`,
    `💎 Added ${data.keys} more keys to my bag! No Tomorrow 3D is heating up 🔥`,
    `🚀 Aping into No Tomorrow 3D with ${data.keys} keys! LFG! 🎲`,
  ],
  win: (data: ShareData) => [
    `🏆 I JUST WON ${data.amount} test FRAME in No Tomorrow 3D! Holy shit! 🤑`,
    `💰 VICTORY! ${data.amount} test FRAME secured in No Tomorrow 3D! The grind pays off! 🎉`,
    `🎯 Just hit the jackpot! ${data.amount} test FRAME from No Tomorrow 3D! 🌙`,
  ],
  dividend: (data: ShareData) => [
    `💸 Just claimed ${data.amount} test FRAME as my key share in No Tomorrow 3D (testnet)! 🔑`,
    `🤑 ${data.amount} test FRAME dividends secured! No Tomorrow 3D keeps on giving! 💎`,
    `📊 Div check: ${data.amount} test FRAME claimed! Love this game! 🎮`,
  ],
  referral: (data: ShareData) => [
    `🤝 My recruits are busy! ${data.amount} test FRAME collected through No Tomorrow 3D refs (testnet)! 🔗`,
    `👥 Referral game strong! Just collected ${data.amount} test FRAME from my frens (testnet)! 🙌`,
  ],
  title: (data: ShareData) => [
    `🏅 Just unlocked the "${data.title}" title in No Tomorrow 3D! Degen status: ACHIEVED 💀`,
    `👑 New title unlocked: ${data.title}! The grind never stops! 🎮`,
  ],
  general: () => [
    `🎮 Playing No Tomorrow 3D - the most degen game on Base! Who's in? 🔥`,
    `💀 No Tomorrow 3D is live! Keys, divs, and chaos. LFG! 🚀`,
    `🎲 Degen hours on No Tomorrow 3D! The FOMO is real! 👀`,
  ],
  story: (data: ShareData) => [
    `📖 THALIA just spoke: "${data.narrative?.slice(0, 120)}..." Something is happening in No Tomorrow 3D 🤖`,
    `🔮 Breaking from the front lines: ${data.title || 'New story event'} in No Tomorrow 3D! THALIA is watching... 👁️`,
    `⚡ Story alert! ${data.title || 'Something big just happened'} in No Tomorrow 3D! The resistance continues 🔥`,
  ],
  streak: (data: ShareData) => [
    `🔥 ${data.streak}-round streak in No Tomorrow 3D! THALIA can't stop me! 💪`,
    `⚡ On a ${data.streak}-round streak! The resistance doesn't sleep! No Tomorrow 3D 🎮`,
    `🔥🔥🔥 ${data.streak} consecutive rounds! Am I addicted? THALIA says yes. 🤖`,
  ],
  bounty: (data: ShareData) => [
    `🎯 Just claimed a THALIA bounty in No Tomorrow 3D! ${data.amount} XP unlocked! The hunt continues 🏹`,
    `💰 Bounty hunter status: ACTIVE. Just cleared "${data.title}" in No Tomorrow 3D! 🎯`,
  ],
  mission: (data: ShareData) => [
    `✅ Mission "${data.title}" complete in No Tomorrow 3D! THALIA is not amused 😤🤖`,
    `🎯 Another mission cleared! "${data.title}" done in No Tomorrow 3D! XP go brrrr 🚀`,
  ],
};

const HASHTAGS = '#NoTomorrow3D #THALIA #Web3Gaming';
const GAME_URL = 'https://nt3d.fun';

export function SocialSharing({ data, className = '', playerAddress }: SocialSharingProps) {
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [copied, setCopied] = useState(false);
  const [shareReward, setShareReward] = useState<string | null>(null);

  const getShareMessage = () => {
    const type = data?.type || 'general';
    const messages = SHARE_MESSAGES[type](data || { type: 'general' });
    return messages[Math.floor(Math.random() * messages.length)];
  };

  // Track social share for XP distribution
  const trackShare = useCallback(async (platform: string) => {
    if (!playerAddress) return;
    try {
      const result = await reportGameEvent(playerAddress, 'social_share', {
        platform,
        shareType: data?.type || 'general',
      });
      if (result.xpEarned > 0) {
        setShareReward(`+${result.xpEarned} XP`);
        setTimeout(() => setShareReward(null), 3000);
      }
    } catch (err) {
      // Silent fail — sharing should still work without tracking
    }
  }, [playerAddress, data?.type]);

  const shareToTwitter = () => {
    const message = getShareMessage();
    const fullMessage = `${message}\n\n${HASHTAGS}\n${GAME_URL}`;
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(fullMessage)}`;
    window.open(twitterUrl, '_blank', 'width=600,height=400');
    trackShare('twitter');
    setShowShareMenu(false);
  };

  const shareToTelegram = () => {
    const message = getShareMessage();
    const telegramUrl = `https://t.me/share/url?url=${encodeURIComponent(GAME_URL)}&text=${encodeURIComponent(message + '\n\n' + HASHTAGS)}`;
    window.open(telegramUrl, '_blank', 'width=600,height=400');
    trackShare('telegram');
    setShowShareMenu(false);
  };

  const copyToClipboard = () => {
    const message = `${getShareMessage()}\n\n${HASHTAGS}\n${GAME_URL}`;
    navigator.clipboard.writeText(message);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shareViaNavigator = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'No Tomorrow 3D',
          text: getShareMessage(),
          url: GAME_URL,
        });
      } catch (error) {
        console.log('Share cancelled');
      }
    } else {
      setShowShareMenu(true);
    }
  };

  return (
    <div className={`relative ${className}`}>
      <motion.button
        onClick={() => setShowShareMenu(!showShareMenu)}
        className="p-2 rounded-lg bg-void-black/50 border border-neon-purple/20 text-gray-400 hover:text-white hover:border-neon-cyan transition-colors"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        title="Share"
      >
        📤
      </motion.button>

      {/* XP Reward Toast */}
      <AnimatePresence>
        {shareReward && (
          <motion.div
            initial={{ opacity: 0, y: 0 }}
            animate={{ opacity: 1, y: -30 }}
            exit={{ opacity: 0, y: -50 }}
            className="absolute -top-2 left-1/2 -translate-x-1/2 text-xs font-bold text-cyan-400 whitespace-nowrap pointer-events-none"
          >
            {shareReward}
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showShareMenu && createPortal(
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowShareMenu(false)}
              className="fixed inset-0 z-40"
            />

            {/* Share Menu */}
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              className="absolute right-0 top-full mt-2 w-48 bg-space-black border border-neon-purple/20 rounded-xl shadow-2xl z-50 overflow-hidden"
            >
              <div className="p-2 space-y-1">
                <ShareButton
                  icon="𝕏"
                  label="Share on X"
                  onClick={shareToTwitter}
                  color="text-white"
                />
                <ShareButton
                  icon="✈️"
                  label="Share on Telegram"
                  onClick={shareToTelegram}
                  color="text-blue-400"
                />
                <ShareButton
                  icon={copied ? '✓' : '📋'}
                  label={copied ? 'Copied!' : 'Copy to Clipboard'}
                  onClick={copyToClipboard}
                  color={copied ? 'text-green-400' : 'text-gray-400'}
                />
                {'share' in navigator && (
                  <ShareButton
                    icon="📱"
                    label="More Options..."
                    onClick={shareViaNavigator}
                    color="text-gray-400"
                  />
                )}
              </div>
            </motion.div>
          </>
        , document.body)}
      </AnimatePresence>
    </div>
  );
}

function ShareButton({ 
  icon, 
  label, 
  onClick, 
  color 
}: { 
  icon: string; 
  label: string; 
  onClick: () => void;
  color: string;
}) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-void-black/50 transition-colors"
    >
      <span className={`text-lg ${color}`}>{icon}</span>
      <span className="text-sm text-gray-300 font-rajdhani">{label}</span>
    </button>
  );
}

// Quick Share Button (for inline use)
interface QuickShareProps {
  message: string;
  className?: string;
}

export function QuickShareTwitter({ message, className = '' }: QuickShareProps) {
  const shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(message + '\n\n' + HASHTAGS + '\n' + GAME_URL)}`;

  return (
    <motion.a
      href={shareUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center gap-2 px-4 py-2 bg-black border border-gray-700 rounded-lg text-white hover:bg-gray-900 transition-colors font-rajdhani text-sm ${className}`}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      𝕏 Share on X
    </motion.a>
  );
}

// Share Achievement Modal
interface ShareAchievementProps {
  isOpen: boolean;
  onClose: () => void;
  achievement: {
    type: 'win' | 'title' | 'milestone';
    title: string;
    description: string;
    value?: string;
  };
}

export function ShareAchievementModal({ isOpen, onClose, achievement }: ShareAchievementProps) {
  const getMessage = () => {
    switch (achievement.type) {
      case 'win':
        return `🏆 ${achievement.title}!\n\n${achievement.description}\n\nValue: ${achievement.value}`;
      case 'title':
        return `🏅 New Title Unlocked: ${achievement.title}!\n\n${achievement.description}`;
      case 'milestone':
        return `🎯 Milestone Achieved: ${achievement.title}!\n\n${achievement.description}`;
      default:
        return `${achievement.title}\n\n${achievement.description}`;
    }
  };

  return ( <BodyPortal>
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div className="glass-panel-3d p-6 max-w-md w-full text-center">
              <div className="text-6xl mb-4">
                {achievement.type === 'win' ? '🏆' : achievement.type === 'title' ? '🏅' : '🎯'}
              </div>
              <h2 className="text-2xl font-orbitron text-neon-cyan mb-2">{achievement.title}</h2>
              <p className="text-gray-400 font-rajdhani mb-4">{achievement.description}</p>
              {achievement.value && (
                <p className="text-xl font-orbitron text-neon-purple mb-6">{achievement.value}</p>
              )}

              <div className="space-y-3">
                <p className="text-sm text-gray-500 font-rajdhani">Share your achievement!</p>
                <div className="flex gap-3 justify-center">
                  <QuickShareTwitter message={getMessage()} />
                  <motion.button
                    onClick={onClose}
                    className="px-4 py-2 bg-void-black/50 border border-neon-purple/20 rounded-lg text-gray-400 hover:text-white transition-colors font-rajdhani text-sm"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Close
                  </motion.button>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  </BodyPortal>);
}

// Referral Link Sharing
export function ReferralLinkShare({ account }: { account: string | null }) {
  const [copied, setCopied] = useState(false);
  const referralLink = account ? `${GAME_URL}?ref=${account}` : '';

  const copyLink = () => {
    if (!referralLink) return;
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!account) return null;

  return (
    <div className="glass-panel-3d p-4">
      <h4 className="text-sm font-orbitron text-neon-cyan mb-2">Your Referral Link</h4>
      <div className="flex gap-2">
        <input
          type="text"
          value={referralLink}
          readOnly
          className="flex-1 bg-void-black/50 border border-neon-purple/20 rounded-lg px-3 py-2 text-sm text-gray-400 font-mono truncate"
        />
        <motion.button
          onClick={copyLink}
          className={`px-4 py-2 rounded-lg font-rajdhani text-sm transition-colors ${
            copied
              ? 'bg-green-500/20 text-green-400 border border-green-500'
              : 'bg-neon-cyan/20 text-neon-cyan border border-neon-cyan hover:bg-neon-cyan/30'
          }`}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          {copied ? '✓ Copied!' : '📋 Copy'}
        </motion.button>
      </div>
      <p className="text-xs text-gray-500 font-rajdhani mt-2">
        Get 5% of the test FRAME your recruits spend (testnet).
      </p>
    </div>
  );
}

